import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Search, ArrowRight, Command as CommandIcon, Landmark, Pickaxe, ShieldAlert } from "lucide-react";
import { NAV_CATALOG } from "@/components/navCatalog";

/**
 * Universal Executive Search — Command Palette.
 * Searches:
 *   1. Every registered module (from navCatalog)
 *   2. Live records — parcels & mineral rights via the Sovereign Intelligence
 *      Centre marketplace endpoint (reused, not duplicated)
 *   3. National risk register entries via GSROS (reused)
 * Presentation-only: navigates to existing routes; never creates records.
 */
export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery(""); setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Live record sources — reuse existing endpoints
  const market = useQuery({
    queryKey: ["palette-market"],
    queryFn: async () => (await api.get("/cadastre360/sovereign/marketplace")).data,
    enabled: open,
    staleTime: 60_000,
  });
  const risks = useQuery({
    queryKey: ["palette-risks"],
    queryFn: async () => (await api.get("/cadastre360/gsros/national-risk-register")).data,
    enabled: open,
    staleTime: 60_000,
  });

  const moduleItems = useMemo(() => NAV_CATALOG.flatMap((d) =>
    d.items.map((it) => ({ ...it, kind: "module", domain: d.label, accent: d.accent }))
  ), []);

  const recordItems = useMemo(() => {
    const rows = [];
    (market.data?.mining_opportunities || []).forEach((m) => rows.push({
      kind: "record", type: "mineral-right",
      icon: Pickaxe, accent: "#C99A2E",
      label: `${m.application_number || m.licence_number || "—"} · ${m.commodity || ""}`.trim(),
      domain: `Mining Cadastre · ${m.province || ""} ${m.district || ""}`.trim(),
      to: "/app/mining",
    }));
    (market.data?.land_opportunities || []).forEach((l) => rows.push({
      kind: "record", type: "parcel",
      icon: Landmark, accent: "#C99A2E",
      label: `${l.parcel_number || "Parcel"} · ${l.land_type || ""}`.trim(),
      domain: `Land Cadastre · ${l.province || ""} ${l.district || ""}`.trim(),
      to: "/app/land",
    }));
    (risks.data?.risks || []).forEach((r) => rows.push({
      kind: "record", type: "risk",
      icon: ShieldAlert, accent: "#E67E7E",
      label: `${r.id} · ${r.risk}`,
      domain: `National Risk Register · ${r.category} · ${r.impact}`,
      to: "/app/gsros",
    }));
    return rows;
  }, [market.data, risks.data]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [...moduleItems, ...recordItems];
    if (!q) return moduleItems.slice(0, 22);
    return all.filter((r) =>
      r.label.toLowerCase().includes(q) ||
      (r.domain || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [moduleItems, recordItems, query]);

  const go = (item) => { setOpen(false); if (item?.to) nav(item.to); };
  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[cursor]); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[8vh]"
         data-testid="command-palette" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-[92%] max-w-2xl rounded-sm border border-[#C99A2E]/40 shadow-2xl overflow-hidden"
           style={{ background: "linear-gradient(180deg, rgba(7,27,43,0.98), rgba(11,53,88,0.95))" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search size={16} className="text-[#C99A2E]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
            onKeyDown={onInputKey}
            placeholder="Search modules, parcels, mineral rights, risks, executive centres…"
            data-testid="command-palette-input"
            className="flex-1 bg-transparent outline-none text-[14px] text-[#F6F8FA] placeholder:text-[#7C93A5]"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-white/10 text-[#7C93A5]">ESC</kbd>
        </div>
        <div className="max-h-[54vh] overflow-y-auto" data-testid="command-palette-results">
          {results.length === 0 && (
            <div className="px-4 py-6 text-[12.5px] text-[#7C93A5] italic">
              No matches. Try "lithium", "parcel", "gsros", "cabinet" or a licence number.
            </div>
          )}
          {results.map((r, i) => {
            const Icon = r.icon;
            return (
              <button
                key={(r.kind || "m") + "-" + (r.to || "") + "-" + r.label + "-" + i}
                onClick={() => go(r)}
                onMouseEnter={() => setCursor(i)}
                data-testid={"command-palette-item-" + (r.to || "").replace(/\//g, "_") + "-" + i}
                className="w-full text-left px-4 py-2.5 flex items-center gap-3 border-b border-white/5"
                style={{ background: i === cursor ? "rgba(201,154,46,0.14)" : "transparent" }}
              >
                <Icon size={14} style={{ color: r.accent }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[#F6F8FA] truncate">{r.label}</div>
                  <div className="text-[10.5px] font-mono uppercase tracking-[0.16em]" style={{ color: r.accent }}>
                    {r.domain}
                  </div>
                </div>
                {r.kind === "record" && (
                  <span className="text-[9px] font-mono uppercase text-[#7C93A5] mr-2">record</span>
                )}
                <ArrowRight size={12} className="text-[#7C93A5]" />
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-white/10 flex items-center gap-3 text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.16em]">
          <span className="inline-flex items-center gap-1"><CommandIcon size={10} /> K</span>
          <span>↑ ↓ navigate</span>
          <span>↵ open</span>
          <span className="ml-auto text-[#C99A2E]">Universal Executive Search</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
